import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Download, Users, Mail } from "lucide-react";

const empty = {
  name: "", country: "", region: "", cuisine: "", diet_type: "vegetarian",
  spice_level: 2, sweetness_level: 1, comfort_score: 70, celebration_score: 50,
  weather_fit: "sunny", prep_time: 30, mood_tags: "happy", meal_type: "lunch",
};

export default function Admin() {
  const [foods, setFoods] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [waitlistStats, setWaitlistStats] = useState(null);
  const [waitlistRecent, setWaitlistRecent] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [verifyTokens, setVerifyTokens] = useState([]);

  const load = async () => {
    const [f, u, v] = await Promise.all([
      api.get("/foods"),
      api.get("/admin/users"),
      api.get("/admin/verification-tokens").catch(() => ({ data: [] })),
    ]);
    setFoods(f.data);
    setUsers(u.data);
    setVerifyTokens(v.data);
  };
  useEffect(() => { load(); }, []);

  const loadWaitlist = async () => {
    setWaitlistLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/waitlist/admin/stats"),
        api.get("/waitlist/admin/recent?limit=50"),
      ]);
      setWaitlistStats(s.data);
      setWaitlistRecent(r.data);
    } catch {
      toast.error("Couldn't load waitlist");
    } finally {
      setWaitlistLoading(false);
    }
  };

  const exportWaitlist = async () => {
    try {
      const res = await api.get("/waitlist/admin/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `yumo-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const openCreate = () => {
    setEditing({ ...empty });
    setOpen(true);
  };

  const openEdit = (food) => {
    setEditing({
      ...food,
      weather_fit: food.weather_fit.join(", "),
      mood_tags: food.mood_tags.join(", "),
      meal_type: food.meal_type.join(", "),
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...editing,
      spice_level: Number(editing.spice_level),
      sweetness_level: Number(editing.sweetness_level),
      comfort_score: Number(editing.comfort_score),
      celebration_score: Number(editing.celebration_score),
      prep_time: Number(editing.prep_time),
      weather_fit: editing.weather_fit.split(",").map((s) => s.trim()).filter(Boolean),
      mood_tags: editing.mood_tags.split(",").map((s) => s.trim()).filter(Boolean),
      meal_type: editing.meal_type.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing.id) await api.put(`/foods/${editing.id}`, payload);
      else await api.post("/foods", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this food?")) return;
    await api.delete(`/foods/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-page">
      <div className="overline mb-3">Admin Console</div>
      <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight">Manage YUMO</h1>

      <Tabs defaultValue="foods" className="mt-10" onValueChange={(v) => { if (v === "waitlist" && waitlistStats === null) loadWaitlist(); }}>
        <TabsList>
          <TabsTrigger value="foods" data-testid="tab-foods">Foods ({foods.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="waitlist" data-testid="tab-waitlist">Waitlist</TabsTrigger>
        </TabsList>

        <TabsContent value="foods" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate} className="rounded-full" data-testid="admin-add-food"><Plus className="w-4 h-4 mr-1" />Add Food</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-serif">{editing?.id ? "Edit" : "Add"} Food</DialogTitle></DialogHeader>
                {editing && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      ["name", "Name"], ["country", "Country"], ["region", "Region"], ["cuisine", "Cuisine"],
                      ["diet_type", "Diet (vegetarian/non-vegetarian/eggitarian/vegan)"],
                      ["spice_level", "Spice (1-5)"], ["sweetness_level", "Sweet (1-5)"],
                      ["comfort_score", "Comfort (0-100)"], ["celebration_score", "Celebration (0-100)"],
                      ["prep_time", "Prep time (min)"],
                      ["weather_fit", "Weather fit (comma)"], ["mood_tags", "Mood tags (comma)"], ["meal_type", "Meal types (comma)"],
                    ].map(([k, lbl]) => (
                      <div key={k}>
                        <Label className="text-xs">{lbl}</Label>
                        <Input value={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} data-testid={`food-field-${k}`} />
                      </div>
                    ))}
                    <Button onClick={save} className="md:col-span-2 rounded-full" data-testid="food-save">Save</Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>

          <Card className="rounded-2xl border-border shadow-none overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Cuisine</TableHead><TableHead>Diet</TableHead>
                  <TableHead>Spice</TableHead><TableHead>Comfort</TableHead><TableHead>Prep</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {foods.slice(0, 50).map((f) => (
                  <TableRow key={f.id} data-testid={`admin-food-row-${f.id}`}>
                    <TableCell className="font-medium">{f.name}</TableCell>
                    <TableCell>{f.cuisine}</TableCell>
                    <TableCell className="capitalize text-sm">{f.diet_type}</TableCell>
                    <TableCell>{f.spice_level}/5</TableCell>
                    <TableCell>{f.comfort_score}</TableCell>
                    <TableCell>{f.prep_time}m</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(f)} data-testid={`edit-${f.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(f.id)} data-testid={`del-${f.id}`}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <Card className="rounded-2xl border-border shadow-none overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Verified</TableHead><TableHead>Provider</TableHead><TableHead>Role</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      {u.email_verified ? (
                        <span className="text-xs font-medium text-green-600">Verified</span>
                      ) : (
                        <span className="text-xs font-medium text-muted-foreground">Pending</span>
                      )}
                    </TableCell>
                    <TableCell className="capitalize text-sm">{u.auth_provider || "email"}</TableCell>
                    <TableCell className="capitalize">{u.role}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.created_at?.slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {verifyTokens.length > 0 && (
            <Card className="rounded-2xl border-border shadow-none mt-6 p-5" data-testid="admin-verify-tokens">
              <div className="overline mb-2">Pending email-verification tokens (MOCKED delivery)</div>
              <p className="text-xs text-muted-foreground mb-3">
                Paste a token below into <code className="text-foreground">/verify-email?token=…</code> to simulate the email click.
              </p>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {verifyTokens.map((t) => (
                  <div key={t.id} className="text-xs flex flex-col md:flex-row md:items-center gap-2 p-2 rounded-lg bg-secondary/50">
                    <span className="font-medium text-foreground min-w-[200px]">{t.email}</span>
                    <code className="text-[10px] break-all text-muted-foreground flex-1">{t.token}</code>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="waitlist" className="mt-6" data-testid="waitlist-tab-content">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <h2 className="font-serif text-2xl font-semibold tracking-tight">Early Access Waitlist</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {waitlistStats?.google_sheets_configured
                  ? "Mirroring to Google Sheets is ACTIVE."
                  : "Google Sheets mirror not configured yet — set GOOGLE_SHEETS_WEBHOOK_URL in backend env."}
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={loadWaitlist} variant="outline" className="rounded-full" data-testid="waitlist-refresh">Refresh</Button>
              <Button onClick={exportWaitlist} className="rounded-full" data-testid="waitlist-export-csv">
                <Download className="w-4 h-4 mr-1.5" />Export CSV
              </Button>
            </div>
          </div>

          {waitlistLoading && <div className="text-sm text-muted-foreground">Loading…</div>}

          {waitlistStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Card className="p-4 rounded-2xl border-border shadow-none" data-testid="waitlist-stat-total">
                <div className="overline flex items-center gap-1.5"><Users className="w-3 h-3" /> Total signups</div>
                <div className="font-serif text-3xl font-semibold tabular-nums mt-1">{waitlistStats.total}</div>
                <div className="text-xs text-muted-foreground mt-1">+{waitlistStats.total_with_baseline - waitlistStats.total} baseline shown publicly</div>
              </Card>
              <Card className="p-4 rounded-2xl border-border shadow-none">
                <div className="overline">Today</div>
                <div className="font-serif text-3xl font-semibold tabular-nums mt-1 text-primary">{waitlistStats.today}</div>
              </Card>
              <Card className="p-4 rounded-2xl border-border shadow-none">
                <div className="overline">This week</div>
                <div className="font-serif text-3xl font-semibold tabular-nums mt-1">{waitlistStats.this_week}</div>
              </Card>
              <Card className="p-4 rounded-2xl border-border shadow-none">
                <div className="overline flex items-center gap-1.5"><Mail className="w-3 h-3" /> Sheets sync</div>
                <div className={`font-serif text-2xl font-semibold mt-1 ${waitlistStats.google_sheets_configured ? "text-green-600" : "text-muted-foreground"}`}>
                  {waitlistStats.google_sheets_configured ? "Active" : "Off"}
                </div>
              </Card>
            </div>
          )}

          <Card className="rounded-2xl border-border shadow-none overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead>
                  <TableHead>Mobile</TableHead><TableHead>City</TableHead><TableHead>Cuisine</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {waitlistRecent.map((r) => (
                  <TableRow key={r.id} data-testid={`waitlist-row-${r.position}`}>
                    <TableCell className="font-mono text-xs text-muted-foreground">#{r.position}</TableCell>
                    <TableCell className="font-medium">{r.full_name}</TableCell>
                    <TableCell className="text-sm">{r.email}</TableCell>
                    <TableCell className="text-sm">{r.mobile || "—"}</TableCell>
                    <TableCell className="text-sm">{r.city || "—"}</TableCell>
                    <TableCell className="text-sm">{r.favorite_cuisine || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{(r.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                  </TableRow>
                ))}
                {!waitlistLoading && waitlistRecent.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No signups yet — share /launch to start collecting.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
